import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, MessageSquare } from "lucide-react";
import { trackCTAClick, trackCallBookingStarted } from "@/lib/analytics";
import { useIsMobile } from "@/hooks/use-mobile";
import { BOOK_A_CALL_URL, scrollToContactForm } from "@/lib/cta";

export function StickyMobileCTA() {
  const isMobile = useIsMobile();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!isMobile) return;

    const hero = document.querySelector("section");
    const form =
      document.getElementById("conversation") ??
      document.getElementById("page-contact");
    if (!hero || !form) return;

    let heroVisible = true;
    let formVisible = false;

    const update = () => setVisible(!heroVisible && !formVisible);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target === hero) heroVisible = entry.isIntersecting;
          if (entry.target === form) formVisible = entry.isIntersecting;
        });
        update();
      },
      { threshold: 0.1 }
    );

    observer.observe(hero);
    observer.observe(form);

    return () => observer.disconnect();
  }, [isMobile]);

  if (!isMobile) return null;

  const onBook = () => {
    trackCTAClick("Book a call", "sticky-mobile");
    trackCallBookingStarted("sticky-mobile");
  };
  const onContact = () => {
    trackCTAClick("Contact us", "sticky-mobile");
    scrollToContactForm();
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ type: "spring", damping: 24, stiffness: 300 }}
          className="fixed bottom-0 left-0 right-0 z-50 p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] bg-background/90 backdrop-blur-md border-t border-border/50"
        >
          <div className="flex items-stretch gap-2">
            <button
              onClick={onContact}
              className="flex-1 flex items-center justify-center gap-2 border border-border bg-background text-foreground font-semibold text-sm py-3 rounded-lg active:scale-[0.98] transition-transform"
            >
              <MessageSquare className="h-4 w-4" />
              Contact us
            </button>
            <a
              href={BOOK_A_CALL_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onBook}
              className="flex-1 flex items-center justify-center gap-2 bg-primary text-primary-foreground font-semibold text-sm py-3 rounded-lg active:scale-[0.98] transition-transform"
            >
              Book a call
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
